import React, { useRef, useState } from "react";
import { UploadCloud, X, FileUp, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { uploadStatement } from "../../api";

const SAMPLE_CSV = `Date,Description,Amount,Balance
04/02/2025,DEPOSIT CUSTOMER DELTA CONSULTING,25500.00,25500.00
04/05/2025,ADOBE CREATIVE CLOUD SUBSCRIPTION,-59.99,25440.01
04/08/2025,TXU ENERGY BILL PAY,-2650.00,22790.01
04/12/2025,GOOGLE ADS BILLING,-3100.00,19690.01
04/15/2025,PROPERTY MGMT GROUP RENT,-9500.00,10190.01
04/22/2025,STAPLES STORE 1102 DALLAS TX,-184.20,10005.81
04/28/2025,GUSTO PAYROLL DIRECT DEP,-31400.00,-21394.19
04/30/2025,DEPOSIT CUSTOMER BETA EVENTS,44000.00,22605.81
`;

const UploadModal = ({ open, onClose, onUploaded, accounts = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const [accountType, setAccountType] = useState("checking");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const runUpload = async (name, content) => {
    if (uploading) return;
    setFileName(name);
    setUploading(true);
    setProgress(15);
    const interval = setInterval(() => setProgress((p) => Math.min(p + 18, 90)), 200);
    try {
      const res = await uploadStatement(name, content, accountType);
      clearInterval(interval);
      setProgress(100);
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
        if (res.status === "ok") {
          onClose();
          toast.success("Statement processed!", { description: res.message });
          onUploaded();
        } else if (res.status === "duplicate") {
          toast.warning("Duplicate upload suppressed", { description: res.message });
        } else if (res.status === "quarantined") {
          toast.error("Statement quarantined", { description: res.message });
        } else {
          toast.error("Upload not processed", { description: res.message });
        }
      }, 400);
    } catch (e) {
      clearInterval(interval);
      setUploading(false);
      setProgress(0);
      toast.error("Upload failed", { description: "The engine could not be reached." });
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("CSV required", {
        description: "The reference engine parses CSV bank exports. PDF/XLSX OCR is a production extraction-tier feature.",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => runUpload(file.name, String(reader.result));
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  return (
    <div
      data-testid="upload-modal"
      className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="glass-panel rounded-md shadow-2xl max-w-md w-full p-6 text-zinc-200 border border-white/10 space-y-4 bg-zinc-900/95"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center space-x-2">
            <UploadCloud className="w-5 h-5 text-blue-400" />
            <h3 className="font-bold text-sm text-white">Upload Statements</h3>
          </div>
          <button data-testid="upload-modal-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-200 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center space-x-2 text-xs">
          <span className="text-zinc-400">Bank account:</span>
          <select
            data-testid="upload-account-select"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
            className="bg-zinc-900 border border-white/20 rounded-md px-2 py-1 text-xs font-semibold text-zinc-300 focus:outline-none cursor-pointer"
          >
            {(accounts.length ? accounts : [{ value: "checking", label: "Checking" }]).map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </div>

        <div
          data-testid="upload-dropzone"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-md p-8 text-center space-y-3 cursor-pointer transition bg-white/[0.04] ${
            dragOver ? "border-blue-500 bg-blue-500/10" : "border-white/20 hover:border-blue-500"
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mx-auto text-blue-400">
            <FileUp className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs font-bold text-white">Drag &amp; drop a CSV bank export here</span>
            <p className="text-[11px] text-zinc-400 mt-1">
              Idempotent ingestion — duplicates are suppressed, injections quarantined
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFile(e.target.files && e.target.files[0])}
          />
        </div>

        <button
          data-testid="upload-sample-btn"
          onClick={() => runUpload("April_2025_Checking.csv", SAMPLE_CSV)}
          disabled={uploading}
          className="w-full bg-zinc-900 hover:bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold py-2 rounded-md transition flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          <ShieldAlert className="w-3.5 h-3.5 text-blue-400" />
          <span>Process Sample April 2025 Statement</span>
        </button>

        {uploading && (
          <div className="space-y-2" data-testid="upload-progress">
            <div className="flex justify-between text-xs text-zinc-400">
              <span>Parsing {fileName}</span>
              <span className="text-blue-400 font-mono">{progress}% {progress === 100 ? "Complete" : "Processing"}</span>
            </div>
            <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
              <div className="bg-blue-500 h-full transition-all duration-200" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadModal;
